import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { LoginForm } from '../types';
import { 
  Card, 
  Button, 
  Input, 
  Label, 
  ErrorMessage, 
  LoadingSpinner,
  colors,
  spacing,
  typography
} from '../styles/GlobalStyles';
import { Trophy, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  padding: ${spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(0, 255, 65, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(0, 255, 65, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 420px;
  padding: ${spacing[10]};
  text-align: center;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.6s ease-out;
  background: rgba(28, 28, 28, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 65, 0.1);
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.3),
    0 10px 10px -5px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(0, 255, 65, 0.1);

  &:hover {
    border-color: rgba(0, 255, 65, 0.2);
    box-shadow: 
      0 25px 30px -5px rgba(0, 0, 0, 0.4),
      0 15px 15px -5px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(0, 255, 65, 0.2);
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[10]};
  color: ${colors.green};
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LogoText = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  margin: 0;
  background: linear-gradient(135deg, ${colors.green} 0%, ${colors.white} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h2`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[3]};
  letter-spacing: -0.025em;
`;

const Subtitle = styled.p`
  color: ${colors.grey[400]};
  margin-bottom: ${spacing[8]};
  font-size: ${typography.fontSize.base};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  text-align: left;
`;

const InputWrapper = styled.div`
  position: relative;
  transition: transform 0.2s ease;

  &:focus-within {
    transform: translateY(-2px);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.grey[400]};
  pointer-events: none;
  transition: color 0.2s ease;
  z-index: 2;

  ${InputWrapper}:focus-within & {
    color: ${colors.green};
  }
`;

const StyledInput = styled(Input)`
  padding: ${spacing[4]} ${spacing[4]} ${spacing[4]} 3rem;
  font-size: ${typography.fontSize.base};
  border-radius: ${spacing[3]};
  border: 2px solid ${colors.grey[700]};
  background: rgba(16, 17, 17, 0.8);
  color: ${colors.white};
  transition: all 0.3s ease;
  width: 100%;

  &::placeholder {
    color: ${colors.grey[500]};
    transition: color 0.2s ease;
  }

  &:focus {
    outline: none;
    border-color: ${colors.green};
    background: rgba(16, 17, 17, 0.95);
    box-shadow: 0 0 0 3px rgba(0, 255, 65, 0.1);
  }

  &:focus::placeholder {
    color: ${colors.grey[400]};
  }

  ${props => props.error && `
    border-color: ${colors.error};
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  `}
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${colors.grey[400]};
  cursor: pointer;
  padding: ${spacing[1]};
  border-radius: ${spacing[1]};
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${colors.white};
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ForgotLink = styled(Link)`
  font-size: ${typography.fontSize.sm};
  color: ${colors.green};
  text-decoration: none;
  align-self: flex-end;
  margin-top: -${spacing[2]};
  transition: all 0.2s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: ${colors.green};
    transition: width 0.2s ease;
  }
  
  &:hover {
    color: ${colors.white};
    
    &::after {
      width: 100%;
    }
  }
`;

const LoginButton = styled(Button)`
  margin-top: ${spacing[4]};
  padding: ${spacing[4]} ${spacing[6]};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  border-radius: ${spacing[3]};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 255, 65, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing[8]} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${colors.grey[700]}, transparent);
  }
  
  &::before {
    margin-right: ${spacing[6]};
  }
  
  &::after {
    margin-left: ${spacing[6]};
  }
`;

const DividerText = styled.span`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const RegisterPrompt = styled.p`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.base};
  margin: 0;
  line-height: 1.6;
`;

const RegisterLink = styled(Link)`
  color: ${colors.green};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  transition: all 0.2s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${colors.green};
    transition: width 0.2s ease;
  }
  
  &:hover {
    color: ${colors.white};
    
    &::after {
      width: 100%;
    }
  }
`;

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<LoginForm>();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: LoginForm) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.password);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Set form errors based on response
      if (error.response?.data?.message?.includes('email')) {
        setError('email', { message: error.response.data.message });
      } else if (error.response?.data?.message?.includes('password')) {
        setError('password', { message: error.response.data.message });
      } else {
        toast.error(error.response?.data?.message || 'Login failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <LoginContainer>
        <LoadingSpinner size="lg" />
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <Trophy size={40} />
          <LogoText>Fantasy Draft</LogoText>
        </Logo>

        <Title>Welcome Back</Title>
        <Subtitle>Sign in to your account to continue your fantasy journey</Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Label htmlFor="email">Email Address</Label>
            <InputWrapper>
              <InputIcon>
                <Mail size={20} />
              </InputIcon>
              <StyledInput
                id="email"
                type="email"
                placeholder="Enter your email address"
                error={!!errors.email}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
            </InputWrapper>
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <InputWrapper>
              <InputIcon>
                <Lock size={20} />
              </InputIcon>
              <StyledInput
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                error={!!errors.password}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </PasswordToggle>
            </InputWrapper>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            <ForgotLink to="/forgot-password">Forgot your password?</ForgotLink>
          </InputGroup>

          <LoginButton
            type="submit"
            disabled={isSubmitting}
            fullWidth
          >
            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Sign In'}
          </LoginButton>
        </Form>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <RegisterPrompt>
          Don't have an account?{' '}
          <RegisterLink to="/register">Create one now</RegisterLink>
        </RegisterPrompt>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;
